import { ReactNode } from 'react';

type FilterableOption = {
  label: ReactNode;
  value: string;
  filterText?: string;
};

type CreateFilterOptions<TOption extends FilterableOption> = {
  stringify?: (input: { data: TOption; label: string; value: string }) => string;
};

export const labelToString = (label: ReactNode): string => (typeof label === 'string' ? label : '');

// Letters that NFKD does not decompose to a base letter, mapped to the ASCII letters they fold to.
const UNFOLDED_LETTERS_BY_BASE: [base: string, letters: string][] = [
  ['a', '\u1e9a\u2c65\u0250'],
  ['aa', '\ua733'],
  ['ae', '\u00e6\u01fd\u01e3'],
  ['ao', '\ua735'],
  ['au', '\ua737'],
  ['av', '\ua739\ua73b'],
  ['ay', '\ua73d'],
  ['b', '\u0180\u0183\u0253'],
  ['c', '\u0188\u023c\ua73f\u2184'],
  ['d', '\u0111\u018c\u0256\u0257\ua77a'],
  ['e', '\u0247\u025b\u01dd'],
  ['f', '\u0192\ua77c'],
  ['g', '\u01e5\u0260\ua7a1\u1d79\ua77f'],
  ['h', '\u0127\u2c68\u2c76\u0265'],
  ['hv', '\u0195'],
  ['i', '\u0268\u0131'],
  ['j', '\u0249'],
  ['k', '\u0199\u2c6a\ua741\ua743\ua745\ua7a3'],
  ['l', '\u0140\u017f\u0142\u019a\u026b\u2c61\ua749\ua781\ua747'],
  ['m', '\u0271\u026f'],
  ['n', '\u019e\u0272\u0149\ua791\ua7a5'],
  ['o', '\u00f8\u01ff\u0254\ua74b\ua74d\u0275'],
  ['oe', '\u0153'],
  ['oi', '\u01a3'],
  ['ou', '\u0223'],
  ['oo', '\ua74f'],
  ['p', '\u01a5\u1d7d\ua751\ua753\ua755'],
  ['q', '\u024b\ua757\ua759'],
  ['r', '\u024d\u027d\ua75b\ua7a7\ua783'],
  ['s', '\u00df\u023f\ua7a9\ua785'],
  ['t', '\u0167\u01ad\u0288\u2c66\ua787'],
  ['tz', '\ua729'],
  ['u', '\u0289'],
  ['v', '\u028b\ua75f\u028c'],
  ['vy', '\ua761'],
  ['w', '\u2c73'],
  ['y', '\u01b4\u024f\u1eff'],
  ['z', '\u01b6\u0225\u0240\u2c6c\ua763'],
];

const baseOfUnfoldedLetter = new Map(
  UNFOLDED_LETTERS_BY_BASE.flatMap(([base, letters]) => {
    return Array.from(letters, (letter): [string, string] => [letter, base]);
  }),
);

const unfoldedLetter = new RegExp(
  `[${UNFOLDED_LETTERS_BY_BASE.map(([, letters]) => letters).join('')}]`,
  'g',
);

const stripDiacritics = (value: string): string => {
  return value
    .replace(unfoldedLetter, (letter) => baseOfUnfoldedLetter.get(letter) ?? letter)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');
};

export const createFilter = <TOption extends FilterableOption = FilterableOption>(
  options: CreateFilterOptions<TOption> = {},
) => {
  const { stringify = ({ data, label, value }) => `${label} ${data.filterText ?? ''} ${value}` } =
    options;

  return (option: TOption, rawInput: string): boolean => {
    const input = rawInput.trim();
    if (!input) return true;

    const candidate = stripDiacritics(
      stringify({
        data: option,
        label: labelToString(option.label),
        value: option.value,
      })
        .toLowerCase()
        .trim(),
    );

    return candidate.includes(stripDiacritics(input.toLowerCase()));
  };
};
